import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Car, Bike, Zap, Truck, Search, Heart, Shield, Clock, Phone, ChevronRight, ChevronLeft, Fuel, Gauge, MapPin, Play, Star, ArrowRight, ArrowLeft, Sparkles, TrendingUp, Users, Settings, Calendar, Hash } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FeaturedVehiclesSection from './FeaturedVehiclesSection';
import DownloadAppBanner from '../components/DownloadAppBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [cities, setCities] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [popup, setPopup] = useState(null);
  const [showPopup, setShowPopup] = useState(true);
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [heroBanners, setHeroBanners] = useState([]);
  const [adBanners, setAdBanners] = useState([]);
  const [guideVideos, setGuideVideos] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [currentBanner, setCurrentBanner] = useState(0);
  const scrollRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  const scrollRow1 = useRef(null);
  const scrollRow2 = useRef(null);

  useEffect(() => {
    fetchCities();
    fetchFAQs();
    fetchPopup();
    fetchFeaturedVehicles();
    fetchTestimonials();
    fetchBanners();
    fetchGuideVideos();
  }, []);

  useEffect(() => {
    fetchPopup();
    fetchBanners();
    fetchFeaturedVehicles(); 
  }, [selectedCity]);

  useEffect(() => {
  if (featuredVehicles.length === 0) return;
  const refs = [scrollRow1, scrollRow2];
  const intervals = refs.map((ref, i) => setInterval(() => {
    if (ref.current) {
      const el = ref.current;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 10) {
        el.scrollLeft = 0;
      } else {
        el.scrollLeft += 320;
      }
    }
  }, 3000 + i * 500));
  return () => intervals.forEach(clearInterval);
}, [featuredVehicles]);

  // Auto-slide hero banners
  useEffect(() => {
    if (heroBanners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentBanner(prev => (prev + 1) % heroBanners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroBanners.length]);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities`);
      setCities(res.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchFAQs = async () => {
    try {
      const res = await axios.get(`${API}/faqs`);
      setFaqs(res.data.faqs);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    }
  };

  const fetchPopup = async () => {
    try {
      // Check if user already dismissed popups in this session
      const dismissedPopups = JSON.parse(sessionStorage.getItem('dismissedPopups') || '[]');
      
      const res = await axios.get(`${API}/popups?city=${selectedCity}`);
      const popups = res.data.popups || [];
      
      // Find the first popup that hasn't been dismissed
      const activePopup = popups.find(p => !dismissedPopups.includes(p.id));
      
      if (activePopup) {
        setPopup(activePopup);
        setShowPopup(true);
      } else {
        setPopup(null);
        setShowPopup(false);
      }
    } catch (error) {
      console.error('Failed to fetch popup:', error);
    }
  };

  // REPLACE this function
const fetchFeaturedVehicles = async () => {
  try {
    const res = await axios.get(`${API}/featured-vehicles?city=${selectedCity}`);
    setFeaturedVehicles(res.data.vehicles);
  } catch (error) {
    console.error('Failed to fetch featured vehicles:', error);
  }
};

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/testimonials`);
      setTestimonials(res.data.testimonials);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API}/banners?city=${selectedCity}`);
      const allBanners = res.data.banners || [];
      // Separate hero banners and ad banners
      setHeroBanners(allBanners.filter(b => b.banner_type === 'hero'));
      setAdBanners(allBanners.filter(b => b.banner_type === 'ad'));
    } catch (error) {
      console.error('Failed to fetch banners:', error);
    }
  };

  const fetchGuideVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos?video_type=guide`);
      setGuideVideos(res.data.videos || []);
    } catch (error) {
      console.error('Failed to fetch guide videos:', error);
    }
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const nextBanner = () => {
    setCurrentBanner(prev => (prev + 1) % heroBanners.length);
  };

  const prevBanner = () => {
    setCurrentBanner(prev => (prev - 1 + heroBanners.length) % heroBanners.length);
  };

  const getFuelColor = (fuel) => {
  if (!fuel) return "text-gray-600";

  const type = fuel.toLowerCase();

  if (type.includes("petrol") || type.includes("cng")) {
    return "text-sky-600"; // Light Blue
  }
  if (type.includes("diesel")) {
    return "text-orange-600"; // Orange
  }
  if (type.includes("electric")) {
    return "text-green-600"; // Green
  }
  if (type.includes("hybrid")) {
    return "text-green-700"; // Green
  }

  return "text-gray-600";
};

const handleSearch = () => {
  if (!searchQuery.trim()) return;

  navigate(`/browse?q=${searchQuery}&city=${selectedCity}`);
};
  // Default hero banners if none from API
  const displayHeroBanners = heroBanners.length > 0 ? heroBanners : [
    { 
      id: '1', 
      image_url: 'https://images.unsplash.com/photo-1766156065651-7215daee1b92?w=1920&h=800&fit=crop',
      title: 'Find Your Perfect Ride',
      subtitle: 'Certified pre-owned vehicles. Trusted sellers. Transparent process.'
    },
    { 
      id: '2', 
      image_url: 'https://images.unsplash.com/photo-1619682817481-e994891cd1f5?w=1920&h=800&fit=crop',
      title: 'Premium Cars Collection',
      subtitle: 'Discover luxury and performance vehicles at best prices.'
    },
    { 
      id: '3', 
      image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&h=800&fit=crop',
      title: 'Bikes & Scooters',
      subtitle: 'Two-wheelers for every budget and style.'
    }
  ];

  // Default testimonials if none from API
  const displayTestimonials = testimonials.length > 0 ? testimonials : [
    { id: '1', name: 'Rajesh K.', city: 'Belgaum', quote: 'Amazing experience! Sold my car within 2 weeks through X7 Autoz. The platform is super easy to use.', thumbnail_url: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=400&h=300&fit=crop' },
    { id: '2', name: 'Priya S.', city: 'Bangalore', quote: 'Found my dream bike at an unbeatable price. The verification process gave me confidence in the purchase.', thumbnail_url: 'https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?w=400&h=300&fit=crop' },
    { id: '3', name: 'Amit P.', city: 'Mumbai', quote: 'As an agent, the bulk upload feature saves me hours every week. Highly recommend for dealers!', thumbnail_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop' }
  ];

  const vehicleCategories = [
    { name: 'Cars', icon: Car, type: 'car', color: 'from-orange-500 to-amber-500', bgColor: 'bg-gradient-to-br from-orange-100 to-amber-50', iconBg: 'bg-gradient-to-br from-gray-500 to-gray-500' },
    { name: 'Bikes', icon: Bike, type: 'bike', color: 'from-slate-700 to-slate-900', bgColor: 'bg-gradient-to-br from-orange-100 to-amber-50', iconBg: 'bg-gradient-to-br from-gray-500 to-gray-500' },
    { name: 'Electric', icon: Zap, type: 'ev', color: 'from-emerald-500 to-teal-500', bgColor: 'bg-gradient-to-br from-orange-100 to-amber-50', iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-500' },
    { name: 'Commercial', icon: Truck, type: 'commercial', color: 'from-blue-500 to-indigo-500', bgColor: 'bg-gradient-to-br from-orange-100 to-amber-50', iconBg: 'bg-gradient-to-br from-gray-500 to-gray-500' }
  ];

  const features = [
    { icon: Shield, title: 'Certified Vehicles', desc: '', gradient: '' },
    { icon: Clock, title: '30-Day Validity', desc: '', gradient: '' },
    { icon: Heart, title: 'Save Favorites', desc: '', gradient: '' },
    { icon: Phone, title: 'Direct Contact', desc: '', gradient: '' }
  ];

  const steps = [
    { num: '1', title: 'Upload Details', desc: 'Fill vehicle information & upload photos', color: 'from-orange-500 to-amber-500' },
    { num: '2', title: 'Make Payment', desc: 'Scan QR code & upload payment screenshot', color: 'from-blue-500 to-indigo-500' },
    { num: '3', title: 'Get Approved', desc: 'Authority verifies & approves your listing', color: 'from-purple-500 to-pink-500' },
    { num: '4', title: 'Go Live', desc: 'Your vehicle is now visible to buyers!', color: 'from-emerald-500 to-green-500' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header selectedCity={selectedCity} setSelectedCity={setSelectedCity} cities={cities} />

 
 
{/* MOBILE BANNER */}
<div className="md:hidden px-4 mt-3">
  <div className="rounded-xl overflow-hidden relative">

    <img
      src={displayHeroBanners[currentBanner]?.image_url}
      className="w-full h-40 object-cover"
      alt="banner"
    />

    {/* DOTS */}
    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
      {displayHeroBanners.map((_, i) => (
        <div
          key={i}
          className={`h-2 w-2 rounded-full ${
            i === currentBanner ? "bg-orange-500" : "bg-white/60"
          }`}
        />
      ))}
    </div>

  </div>
</div>

{/* MOBILE SEARCH BAR */}
<div className="md:hidden px-4 mt-4 mb-4">
  <div className="flex items-center bg-white shadow-md rounded-full px-4 py-3">

    <Search 
      className="h-5 w-5 text-gray-500 mr-2 cursor-pointer"
      onClick={handleSearch}
    />

    <input
      type="text"
      placeholder="Search cars, bikes..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleSearch();
      }}
      className="flex-1 outline-none text-sm bg-transparent"
    />

  </div>
</div>

      {/* Popup Dialog */}
      {popup && showPopup && (
        <Dialog open={showPopup} onOpenChange={(open) => {
          if (!open && popup) {
            // Store dismissed popup ID in session
            const dismissedPopups = JSON.parse(sessionStorage.getItem('dismissedPopups') || '[]');
            dismissedPopups.push(popup.id);
            sessionStorage.setItem('dismissedPopups', JSON.stringify(dismissedPopups));
          }
          setShowPopup(open);
        }}>
          <DialogContent className="max-w-md p-0 overflow-hidden rounded-2xl" data-testid="home-popup-dialog">
            {popup.image && (
              <div className="w-full h-48 overflow-hidden">
                <img src={popup.image} alt={popup.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-3">{popup.title}</h2>
              <p className="text-gray-600 mb-4 whitespace-pre-line">{popup.content}</p>
              <div className="flex gap-3">
                {popup.link_url && (
                  <Button
                    onClick={() => window.open(popup.link_url, '_blank')}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 border-0"
                  >
                    {popup.link_text || 'Learn More'}
                  </Button>
                )}
                <Button 
                  variant={popup.link_url ? 'outline' : 'default'}
                  onClick={() => {
                    const dismissedPopups = JSON.parse(sessionStorage.getItem('dismissedPopups') || '[]');
                    dismissedPopups.push(popup.id);
                    sessionStorage.setItem('dismissedPopups', JSON.stringify(dismissedPopups));
                    setShowPopup(false);
                  }} 
                  className={popup.link_url ? '' : 'w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 border-0'}
                  data-testid="close-popup-btn"
                >
                  {popup.link_url ? 'Cancel' : 'Close'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Hero Section with Sliding Banners */}
      <section className="hidden md:block relative h-[550px] md:h-[650px] overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(251,146,60,0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(59,130,246,0.1),transparent_50%)]" />
        </div>
        
        <AnimatePresence mode="wait">
          <motion.div
            key={currentBanner}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0"
          >
            <img
              src={displayHeroBanners[currentBanner]?.image_url}
              alt={displayHeroBanners[currentBanner]?.title || 'Hero'}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
          </motion.div>
        </AnimatePresence>
        
        <div className="relative z-10 h-full flex items-center px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-2xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
              >
                <Sparkles className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-white/90 font-medium">Trusted by 10,000+ buyers & sellers</span>
              </motion.div>
              
              <motion.h1
                key={`title-${currentBanner}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              >
                Find Your Perfect
                <span className="block bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                  Ride Today
                </span>
              </motion.h1>
              
              <motion.p
                key={`subtitle-${currentBanner}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed"
              >
                {displayHeroBanners[currentBanner]?.subtitle || 'Certified pre-owned vehicles. Trusted sellers. Transparent process.'}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex gap-4 flex-wrap"
              >
                <Button
                  size="lg"
                  onClick={() => navigate('/browse')}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/30 px-8 border-0 group"
                  data-testid="browse-vehicles-btn"
                >
                  <Search className="mr-2 h-5 w-5" />
                  Browse Vehicles
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate(user ? '/upload-vehicle' : '/login')}
                  className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 hover:border-white/50"
                  data-testid="sell-vehicle-btn"
                >
                  Sell Your Vehicle
                </Button>
              </motion.div>
              
              {/* Stats Row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex gap-8 mt-10 pt-8 border-t border-white/10"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-400" />
                    <span className="text-2xl font-bold text-white">500+</span>
                  </div>
                  <p className="text-sm text-white/60">Active Listings</p>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-400" />
                    <span className="text-2xl font-bold text-white">1000+</span>
                  </div>
                  <p className="text-sm text-white/60">Happy Customers</p>
                </div>
                <div className="hidden sm:block">
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-400" />
                    <span className="text-2xl font-bold text-white">4.8</span>
                  </div>
                  <p className="text-sm text-white/60">Average Rating</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Banner Navigation Arrows */}
        {displayHeroBanners.length > 1 && (
          <>
            <button
              onClick={prevBanner}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center transition-all hover:scale-110 border border-white/20"
              data-testid="prev-banner-btn"
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
            <button
              onClick={nextBanner}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center transition-all hover:scale-110 border border-white/20"
              data-testid="next-banner-btn"
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>
          </>
        )}

        {/* Banner Dots Indicator */}
        {displayHeroBanners.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {displayHeroBanners.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentBanner(idx)}
                className={`h-2 rounded-full transition-all ${
                  idx === currentBanner ? 'bg-orange-500 w-8' : 'bg-white/40 hover:bg-white/60 w-2'
                }`}
                data-testid={`banner-dot-${idx}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Ad Banner Strip */}
      {adBanners.length > 0 && (
        <section className="bg-secondary-100">
          <a
            href={adBanners[0]?.link_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <div className="max-w-7xl mx-auto py-2 px-4">
              <div className="flex items-center justify-center gap-4">
                {adBanners[0]?.image_url && (
                  <img 
                    src={adBanners[0].image_url} 
                    alt={adBanners[0].title || 'Advertisement'} 
                    className="h-12 md:h-16 object-contain"
                  />
                )}
                <div className="text-center">
                  <p className="text-sm md:text-base font-medium text-secondary-800">
                    {adBanners[0]?.title}
                  </p>
                  {adBanners[0]?.subtitle && (
                    <p className="text-xs md:text-sm text-secondary-600">{adBanners[0].subtitle}</p>
                  )}
                </div>
              </div>
            </div>
          </a>
        </section>
      )}

      <FeaturedVehiclesSection selectedCity={selectedCity} />

      {/* Vehicle Categories */}
      <section className="py-16 md:py-20 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto text-center">
            
            {/* शीर्ष */}
            <p className="text-xs tracking-widest text-gray-500 mb-3">
              Browse Categories
            </p>

            <h2 className="text-2xl md:text-4xl font-semibold text-gray-900 mb-10">
              What Are You Looking For?
            </h2>

            {/* GRID */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {vehicleCategories.map((cat) => {
                const Icon = cat.icon;

                return (
                  <div
            key={cat.type}
            onClick={() => navigate(`/browse?type=${cat.type}`)}
            className="group cursor-pointer bg-white shadow-xl rounded-2xl p-8 flex flex-col items-center justify-center relative overflow-hidden hover:shadow-2xl"
          >

            {/* ICON */}
            <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mb-4
              group-hover:absolute group-hover:top-1/2 group-hover:left-1/2
              group-hover:-translate-x-1/2 group-hover:-translate-y-1/2
              group-hover:scale-125">
              
              <Icon className="h-6 w-6 text-white group-hover:h-10 group-hover:w-10" />
            </div>

            {/* TITLE */}
            <h3 className="text-lg font-semibold text-gray-900 group-hover:hidden">
              {cat.name}
            </h3>

          </div>
        );
      })}
    </div>

      </div>
    </section>

    <DownloadAppBanner />

      {/* Why Choose X7 Autoz */}
      <section className="py-20 md:py-24 px-4 md:px-8 lg:px-12 bg-white relative overflow-hidden">
  
            {/* Subtle premium glow background */}
            <div className="absolute inset-0">
              <div className="absolute top-0 left-1/2 w-[500px] h-[500px] bg-indigo-500/10 blur-[120px] -translate-x-1/2" />
            </div>

            <div className="max-w-7xl mx-auto relative">
              
              {/* HEADER */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-14 md:mb-16"
              >
                

                <h2 className="text-3xl md:text-5xl font-semibold text-black mb-4">
                  Why Choose X7 Autoz?
                </h2>

                <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                  Experience premium features designed for your convenience
                </p>
              </motion.div>

              {/* CARDS */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {features.map((feature, idx) => {
                  const Icon = feature.icon;

                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.08 }}
                      viewport={{ once: true }}
                      className="group"
                    >
                      <div className="h-full bg-black backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 shadow-[0_10px_30px_rgba(0,0,0,0.3)] text-center">

                        {/* ICON (UNCHANGED) */}
                        <div
                          className={`w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-5 mx-auto shadow-md group-hover:scale-105 transition-transform duration-300`}
                        >
                          <Icon className="h-7 w-7 md:h-8 md:w-8 text-black" />
                        </div>

                        {/* TITLE */}
                        <h3 className="text-lg md:text-xl font-semibold text-white mb-2">
                          {feature.title}
                        </h3>

                        {/* DESCRIPTION */}
                        <p className="text-sm text-slate-400 leading-relaxed">
                          {feature.desc}
                        </p>

                      </div>
                    </motion.div>
                  );
                })}
              </div>

            </div>
          </section>

      {/* Guide Videos Section */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-semibold text-secondary-900 text-center mb-4">
            How It Works
          </h2>
          <p className="text-center text-secondary-600 mb-8 md:mb-12">Watch our guide videos to learn how to buy and sell vehicles</p>
          
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {(guideVideos.length > 0 ? guideVideos.slice(0, 3) : [
              { id: '1', title: 'How to Buy a Vehicle', description: 'Step-by-step guide for buyers', youtube_url: 'https://www.youtube.com/watch?v=HME2P79MAIo' },
              { id: '2', title: 'How to Sell Your Vehicle', description: 'Complete sellers guide', youtube_url: 'https://www.youtube.com/watch?v=xpCAraaoy2w' },
              { id: '3', title: 'Agent Registration Guide', description: 'Become a verified agent', youtube_url: '' }
            ]).map((video, idx) => {
              const videoId = extractYouTubeId(video.youtube_url);
              return (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="overflow-hidden hover:shadow-xl transition-shadow" data-testid={`guide-video-${idx}`}>
                    <div className="aspect-video bg-secondary-200 relative">
                      {videoId ? (
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-primary-200 to-primary-300">
                          <Play className="h-12 w-12 text-primary-600 mb-2" />
                          <span className="text-primary-700 text-sm">Video Coming Soon</span>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4 md:p-6">
                      <h3 className="font-semibold text-lg text-secondary-800 mb-1">{video.title}</h3>
                      {video.description && (
                        <p className="text-secondary-600 text-sm">{video.description}</p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
          
          <div className="text-center mt-8">
            <Button 
              onClick={() => navigate('/contact')}
              variant="outline"
              className="border-primary-500 text-primary-600 hover:bg-primary-50"
            >
              View All Videos & FAQs
            </Button>
          </div>
        </div>
      </section>

      {/* Video Testimonials */}
      <section className="py-20 px-4 md:px-8 bg-[#f3f3f3] text-black">
          <div className="w-full px-4 md:px-8">

            {/* HEADER */}
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-3xl md:text-5xl font-semibold">
                What Our Customer Say
              </h2>

              {/* NAV BUTTONS */}
              <div className="flex gap-3">
                <button
                  onClick={() =>
                    scrollRef.current.scrollBy({ left: -500, behavior: "smooth" })
                  }
                  className="w-11 h-11 rounded-xl border border-gray-400 flex items-center justify-center hover:bg-white/10 transition"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>

                <button
                  onClick={() =>
                    scrollRef.current.scrollBy({ left: 500, behavior: "smooth" })
                  }
                  className="w-11 h-11 rounded-xl bg-orange-500 flex items-center justify-center hover:bg-orange-600 transition"
                >
                  <ArrowRight className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>

            {/* SLIDER */}
            <div
              ref={scrollRef}
              className="flex gap-6 overflow-x-auto overflow-y-visible scroll-smooth no-scrollbar py-4"
            >
              {displayTestimonials.map((testimonial, idx) => (
                <div
                  key={testimonial.id}
                  className="flex-shrink-0 w-[320px] md:w-[32%] bg-transparent border border-gray-300 rounded-2xl p-6 md:p-7 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  {/* VIDEO / IMAGE */}
                  <div className="aspect-video rounded-xl overflow-hidden mb-5 relative">
                    <img
                      src={
                        testimonial.thumbnail_url ||
                        `https://images.unsplash.com/photo-155183602${
                          idx + 2
                        }-78fc866c54c1?w=400`
                      }
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />

                    <div
                      className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer hover:bg-black/40 transition"
                      onClick={() =>
                        testimonial.video_url &&
                        setPlayingVideo(testimonial.video_url)
                      }
                    >
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center hover:scale-110 transition">
                        <Play className="h-5 w-5 text-black ml-1" />
                      </div>
                    </div>
                  </div>

                  {/* QUOTE */}
                  <p className="text-black italic mb-6 leading-relaxed line-clamp-4">
                    "{testimonial.quote}"
                  </p>

                  {/* USER */}
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center font-semibold text-lg">
                      {testimonial.name.charAt(0)}
                    </div>

                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-slate-400">
                        {testimonial.city}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* VIDEO DIALOG (UNCHANGED) */}
          <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
            <DialogContent className="max-w-4xl p-0 overflow-hidden">
              <div className="aspect-video">
                {playingVideo && (
                  <iframe
                    src={playingVideo}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>
      </section>

    {/* How to Sell */}
    <section className="relative py-24 px-4 md:px-8 bg-black text-white overflow-hidden">

          <div className="max-w-7xl mx-auto">

            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-16">
              
              <h2 className="text-4xl md:text-6xl font-semibold leading-tight tracking-tight">
                <span className="block text-white">A Radially</span>
                <span className="block text-white/70">Simple Journey</span>
              </h2>

              <p className="text-sm md:text-base text-white/50 max-w-sm mt-6 md:mt-0">
                We stripped away the noise. Four steps to ownership, defined by absolute transparency.
              </p>
            </div>

            {/* Divider Line */}
            <div className="w-full h-px bg-white/10 mb-16" />

            {/* STEPS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">

              {steps.map((step, idx) => (
                <div
                  key={idx}
                  className={`relative ${
                    idx === 1 ? "lg:mt-16" : ""
                  } ${idx === 3 ? "lg:mt-16" : ""}`}
                >

                  {/* NUMBER (NOT BACKGROUND NOW) */}
                  <div className="text-[60px] md:text-[80px] font-bold text-white mb-4 drop-shadow-[0_12px_18px_rgba(255,255,255,0.18)]">
                    {step.num}
                  </div>

                  {/* CONTENT */}
                  <div>
                    <h3 className="text-sm tracking-widest text-white mb-2 uppercase">
                      {step.title}
                    </h3>

                    <p className="text-white/50 text-sm leading-relaxed max-w-xs">
                      {step.desc}
                    </p>
                  </div>

                </div>
              ))}

            </div>

          {/* CTA */}
          <div className="mt-20">
            <button
              onClick={() => navigate(user ? '/upload-vehicle' : '/login')}
              className="text-white border border-white/20 px-8 py-4 rounded-lg hover:bg-white hover:text-black transition-all flex items-center gap-2"
            >
              Start Selling Now
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>

        </div>
      </section>

      {/* FAQs */}
      {faqs.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-white">
            <div className="max-w-4xl mx-auto">
              
              <h2 className="text-2xl md:text-4xl font-semibold text-center mb-10">
                Frequently Asked Questions
              </h2>

              <div className="divide-y divide-gray-300">
                {faqs.map((faq, idx) => (
                  <details
                    key={faq.id}
                    className="group py-5"
                  >
                    {/* QUESTION */}
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <span className="font-medium text-gray-900 text-base md:text-lg">
                        {faq.question}
                      </span>

                      {/* + / - ICON */}
                      <span className="text-2xl font-light transition-transform duration-200 group-open:rotate-45">
                        +
                      </span>
                    </summary>

                    {/* ANSWER */}
                    <p className="mt-3 text-gray-600 text-sm md:text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>

            </div>
          </section>
        )}
      <Footer />
    </div>
  );
};

export default Home;
